import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  TrendingUp, 
  DollarSign, 
  Calendar,
  MapPin,
  Building,
  Users,
  Eye,
  Download,
  RefreshCw,
  AlertCircle,
  Activity
} from 'lucide-react';

// Type definitions
interface QuickStats {
  totalWorks: number;
  totalSanctioned: number;
  totalSpent: number;
  utilizationPercentage: number;
  completedWorks: number;
  delayedWorks: number;
  pendingBillsAmount: number;
}

interface DashboardData {
  overview: {
    total_works: number;
    total_sanctioned: number;
    total_ras: number;
    total_spent: number;
    pending_with_pao: number;
    work_done_not_billed: number;
    avg_work_cost: number;
    utilization_percentage: number;
    pending_bills_percentage: number;
  };
  grantTypeAnalysis: Array<{
    oas_grant_level: string;
    grant_type: string;
    work_count: number;
    total_sanctioned: number;
    total_spent: number;
    avg_cost: number;
  }>;
  trendAnalysis: Array<{
    month: string;
    works_sanctioned: number;
    amount_sanctioned: number;
    amount_spent: number;
  }>;
}

interface ProgressData {
  physicalProgress: Array<{
    district_id: number;
    district_name: string;
    total_sanctioned_length: number;
    total_completed_length: number;
    total_works: number;
    completion_percentage: number;
  }>;
  financialProgress: Array<{
    abstract_stage_of_work: string;
    work_count: number;
    total_sanctioned: number;
    total_spent: number;
    financial_progress: number;
  }>;
  delayedWorks: Array<{
    work_id: number;
    sno: number;
    work_name: string;
    agency_name: string;
    completion_date_set: string;
    abstract_stage_of_work: string;
    estimate_cost: number;
    cum_exp_on_work: number;
    district_name: string;
    mandal_name: string;
  }>;
  pendingTasks?: {
    tsToBeAccorded: number;
    designsPending: number;
    tendersToCall: number;
    agreementsPending: number;
    crApprovalsPending: number;
    finalPaymentsPending: number;
    progressUpdatesDue: number;
  };
}

interface StageSummary {
  abstract_stage_of_work: string;
  work_count: number;
  total_estimate_cost: number;
  total_expenditure: number;
  avg_estimate_cost: number;
  total_length_sanctioned: number;
  total_length_completed: number;
}

interface DistrictSummary {
  district_id: number;
  district_name: string;
  total_works: number;
  total_estimate_cost: number;
  total_ras_amount: number;
  total_expenditure: number;
  total_length_sanctioned: number;
  total_length_completed: number;
  total_bills_pending: number;
  total_work_not_recorded: number;
  avg_estimate_cost: number;
  financial_progress_percentage: number;
  physical_progress_percentage: number;
  avg_completion_percentage: number;
}

interface Work {
  work_id: number;
  sno: number;
  work_name: string;
  estimate_cost: number;
  cum_exp_on_work: number;
  abstract_stage_of_work: string;
  district_name: string;
  mandal_name: string;
}

// API Services with proper typing
class WorksApiService {
  private baseUrl: string = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';
  
  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}), ...(options.headers || {}) },
      ...options
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async getAllWorks(filters: Record<string, any> = {}): Promise<any> {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/works${params ? `?${params}` : ''}`);
  }

  async getFinancialDashboard(): Promise<DashboardData> {
    return this.request('/works/dashboard/financial');
  }

  async getProgressTracking(): Promise<ProgressData> {
    return this.request('/works/dashboard/progress-tracking');
  }

  async getDistrictWiseSummary(): Promise<DistrictSummary[]> {
    return this.request('/works/summary/district-wise');
  }

  async getStageWiseSummary(): Promise<StageSummary[]> {
    return this.request('/works/summary/stage-wise');
  }

  async getQuickStats(): Promise<QuickStats> {
    return this.request('/works/stats/quick');
  }

  async getPendingBillsReport(): Promise<Work[]> {
    return this.request('/works/reports/pending-bills');
  }

  async exportToExcel(filters: Record<string, any> = {}): Promise<any> {
    return this.request('/works/export/excel', { method: 'POST', body: JSON.stringify(filters) });
  }

  async healthCheck(): Promise<any> {
    return this.request('/works/health/check');
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }
}

class MasterDataAPIService {
  private baseUrl: string = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';
  
  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('token') ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}), ...(options.headers || {}) },
      ...options
    });
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  async getDistrictsWithStats(): Promise<any> {
    return this.request('/master-data/districts/stats');
  }

  async getMasterDataStats(): Promise<any> {
    return this.request('/master-data/stats');
  }

  async healthCheck(): Promise<any> {
    return this.request('/master-data/health');
  }
}

// Initialize services
const worksApi = new WorksApiService();
const masterDataApi = new MasterDataAPIService();

// Component Props Interfaces
interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  subtitle?: string;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';
  trend?: number;
  loading?: boolean;
  onClick?: () => void;
}

interface TaskCardProps {
  title: string;
  value: string | number;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';
  urgent?: boolean;
  onClick?: () => void;
}

interface ErrorAlertProps {
  message: string;
  onRetry?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ 
  icon: Icon, 
  title, 
  value, 
  subtitle, 
  color = 'blue', 
  trend, 
  loading = false, 
  onClick 
}) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700'
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg border-2 border-gray-200 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
          <div className="w-16 h-4 bg-gray-300 rounded"></div>
        </div>
        <div className="mt-4 w-24 h-8 bg-gray-300 rounded"></div>
        <div className="mt-2 w-32 h-4 bg-gray-300 rounded"></div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-white p-6 rounded-lg border-2 ${colorClasses[color]} shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer transform hover:scale-105' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <Icon className="h-8 w-8" />
        {trend !== undefined && (
          <span className={`text-sm font-medium flex items-center ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
            <TrendingUp className={`h-3 w-3 ml-1 ${trend < 0 ? 'rotate-180' : ''}`} />
          </span>
        )}
      </div>
      <div className="mt-4">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const TaskCard: React.FC<TaskCardProps> = ({ 
  title, 
  value, 
  color = 'blue', 
  urgent = false, 
  onClick 
}) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700'
  };

  return (
    <div 
      className={`p-4 rounded-lg border-2 ${colorClasses[color]} ${urgent ? 'ring-2 ring-red-400 ring-opacity-50' : ''} hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">{title}</h4>
        {urgent && <AlertCircle className="h-4 w-4 text-red-500" />}
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
      {onClick && (
        <div className="mt-2 text-xs text-gray-500 flex items-center">
          <Eye className="h-3 w-3 mr-1" />
          View Details
        </div>
      )}
    </div>
  );
};

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center p-8">
    <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
    <span className="ml-2 text-gray-600">Loading dashboard data...</span>
  </div>
);

const ErrorAlert: React.FC<ErrorAlertProps> = ({ message, onRetry }) => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
        <span className="text-red-800">{message}</span>
      </div>
      {onRetry && (
        <button 
          onClick={onRetry}
          className="text-red-600 hover:text-red-800 underline text-sm"
        >
          Retry
        </button>
      )}
    </div>
  </div>
);

const PReDDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [districtSummary, setDistrictSummary] = useState<DistrictSummary[]>([]);
  const [stageSummary, setStageSummary] = useState<StageSummary[]>([]);
  const [pendingBills, setPendingBills] = useState<Work[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const loadDashboardData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Parallel API calls for better performance
      const [
        dashboard,
        progress,
        stats,
        districts,
        stages,
        bills
      ] = await Promise.all([
        worksApi.getFinancialDashboard().catch(() => ({ 
          overview: {
            total_works: 0,
            total_sanctioned: 0,
            total_ras: 0,
            total_spent: 0,
            pending_with_pao: 0,
            work_done_not_billed: 0,
            avg_work_cost: 0,
            utilization_percentage: 0,
            pending_bills_percentage: 0
          }, 
          trendAnalysis: [], 
          grantTypeAnalysis: [] 
        } as DashboardData)),
        worksApi.getProgressTracking().catch(() => ({ 
          physicalProgress: [], 
          financialProgress: [], 
          delayedWorks: [],
          pendingTasks: {
            tsToBeAccorded: 0,
            designsPending: 0,
            tendersToCall: 0,
            agreementsPending: 0,
            crApprovalsPending: 0,
            finalPaymentsPending: 0,
            progressUpdatesDue: 0
          }
        } as ProgressData)),
        worksApi.getQuickStats().catch(() => ({ 
          totalWorks: 0, 
          totalSanctioned: 0, 
          totalSpent: 0,
          utilizationPercentage: 0,
          completedWorks: 0,
          delayedWorks: 0,
          pendingBillsAmount: 0
        } as QuickStats)),
        worksApi.getDistrictWiseSummary().catch(() => [] as DistrictSummary[]),
        worksApi.getStageWiseSummary().catch(() => [] as StageSummary[]),
        worksApi.getPendingBillsReport().catch(() => [] as Work[])
      ]);

      setDashboardData(dashboard);
      setProgressData(progress);
      setQuickStats(stats);
      setDistrictSummary(districts);
      setStageSummary(stages);
      setPendingBills(bills);
      setLastUpdated(new Date());
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to load dashboard data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleExportData = async (): Promise<void> => {
    try {
      await worksApi.exportToExcel();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      alert(`Export failed: ${errorMessage}`);
    }
  };

  const calculateProgressStats = () => {
    if (!stageSummary.length) return { completed: 0, inProgress: 0, notStarted: 0, onHold: 0 };
    
    return stageSummary.reduce((acc, stage) => {
      switch (stage.abstract_stage_of_work) {
        case 'Completed':
          acc.completed += stage.work_count;
          break;
        case 'In Progress':
          acc.inProgress += stage.work_count;
          break;
        case 'Not Started':
          acc.notStarted += stage.work_count;
          break;
        case 'On Hold':
          acc.onHold += stage.work_count;
          break;
        default:
          break;
      }
      return acc;
    }, { completed: 0, inProgress: 0, notStarted: 0, onHold: 0 });
  };

  const progressStats = calculateProgressStats();
  const totalWorks = progressStats.completed + progressStats.inProgress + progressStats.notStarted + progressStats.onHold;
  
  const pieChartData = [
    { name: 'Completed', value: progressStats.completed, color: '#10B981' },
    { name: 'In Progress', value: progressStats.inProgress, color: '#3B82F6' },
    { name: 'Not Started', value: progressStats.notStarted, color: '#F59E0B' },
    { name: 'On Hold', value: progressStats.onHold, color: '#EF4444' }
  ];

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">PRED Works Dashboard</h1>
            <p className="text-gray-600">Abstract Status of PRED Works - Financial Year 2024-25</p>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated.toLocaleString()}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={loadDashboardData}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExportData}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {error && <ErrorAlert message={error} onRetry={loadDashboardData} />}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={FileText}
          title="Total Works"
          value={quickStats?.totalWorks?.toLocaleString() || '0'}
          subtitle="All sanctioned works"
          color="blue"
          loading={loading}
        />
        <StatCard
          icon={DollarSign}
          title="Total Sanctioned"
          value={quickStats?.totalSanctioned ? worksApi.formatCurrency(quickStats.totalSanctioned) : '₹0'}
          subtitle="Total allocation"
          color="green"
          loading={loading}
        />
        <StatCard
          icon={TrendingUp}
          title="Total Expenditure"
          value={quickStats?.totalSpent ? worksApi.formatCurrency(quickStats.totalSpent) : '₹0'}
          subtitle="Amount utilized"
          color="orange"
          loading={loading}
        />
        <StatCard
          icon={Activity}
          title="Utilization"
          value={`${quickStats?.utilizationPercentage?.toFixed(1) || '0'}%`}
          subtitle="Financial progress"
          color="purple"
          loading={loading}
        />
      </div>

      {/* Work Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Progress Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Status Distribution</h3>
          {totalWorks > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Works']} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Status Cards */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Current Status</h3>
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              icon={CheckCircle}
              title="Completed"
              value={progressStats.completed.toLocaleString()}
              color="green"
              loading={loading}
            />
            <StatCard
              icon={Clock}
              title="In Progress"
              value={progressStats.inProgress.toLocaleString()}
              color="blue"
              loading={loading}
            />
            <StatCard
              icon={AlertTriangle}
              title="Not Started"
              value={progressStats.notStarted.toLocaleString()}
              color="orange"
              loading={loading}
            />
            <StatCard
              icon={XCircle}
              title="On Hold"
              value={progressStats.onHold.toLocaleString()}
              color="red"
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* Pending Tasks */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Pending Tasks to be Monitored</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <TaskCard
            title="TS to be Accorded"
            value={progressData?.pendingTasks?.tsToBeAccorded || 0}
            color="red"
            urgent={true}
            onClick={() => console.log('TS to be accorded clicked')}
          />
          <TaskCard
            title="Designs Pending"
            value={progressData?.pendingTasks?.designsPending || 0}
            color="orange"
            onClick={() => console.log('Designs pending clicked')}
          />
          <TaskCard
            title="Tenders to be Called"
            value={progressData?.pendingTasks?.tendersToCall || 0}
            color="blue"
            onClick={() => console.log('Tenders to be called clicked')}
          />
          <TaskCard
            title="Agreements Pending"
            value={progressData?.pendingTasks?.agreementsPending || 0}
            color="purple"
            onClick={() => console.log('Agreements pending clicked')}
          />
          <TaskCard
            title="CR Approvals Pending"
            value={progressData?.pendingTasks?.crApprovalsPending || 0}
            color="red"
            urgent={true}
            onClick={() => console.log('CR approvals pending clicked')}
          />
          <TaskCard
            title="Final Payments Pending"
            value={progressData?.pendingTasks?.finalPaymentsPending || 0}
            color="orange"
            onClick={() => console.log('Final payments pending clicked')}
          />
          <TaskCard
            title="Progress Updates Due"
            value={progressData?.pendingTasks?.progressUpdatesDue || 0}
            color="gray"
            onClick={() => console.log('Progress updates due clicked')}
          />
          <TaskCard
            title="EOATs Overdue"
            value={progressData?.delayedWorks?.length || 0}
            color="red"
            urgent={true}
            onClick={() => console.log('EOATs overdue clicked')}
          />
        </div>
      </div>

      {/* Grant Type Analysis */}
      {dashboardData?.grantTypeAnalysis && dashboardData.grantTypeAnalysis.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grant Type Analysis</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboardData.grantTypeAnalysis}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="grant_type" />
              <YAxis />
              <Tooltip formatter={(value, name) => {
                if (name === 'total_sanctioned' || name === 'total_spent') {
                  return [worksApi.formatCurrency(value as number), name];
                }
                return [value, name];
              }} />
              <Bar dataKey="work_count" fill="#3B82F6" name="Work Count" />
              <Bar dataKey="total_sanctioned" fill="#10B981" name="Sanctioned Amount" />
              <Bar dataKey="total_spent" fill="#F59E0B" name="Amount Spent" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* District Performance */}
      {districtSummary.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Districts by Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">District</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Works</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sanctioned Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expenditure</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress %</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {districtSummary.slice(0, 10).map((district) => (
                  <tr key={district.district_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {district.district_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {district.total_works}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {worksApi.formatCurrency(district.total_expenditure)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(district.financial_progress_percentage || 0, 100)}%` }}
                          ></div>
                        </div>
                        <span>{(district.financial_progress_percentage || 0).toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Health Status */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600">API Status: Healthy</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600">Database: Connected</span>
          </div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-gray-400 mr-1" />
            <span className="text-sm text-gray-600">Last sync: {lastUpdated.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PReDDashboard;